import { useState } from 'react';
import { useAuth } from '../lib/auth.jsx';
import { useData } from '../lib/data.jsx';
import { useUI, Sec, Row, Pimg } from '../lib/ui.jsx';
import { rpc } from '../lib/supabase.js';
import { B, todayISO, parseD, iso, thDate, TH_M, TH_D } from '../lib/util.js';
import { BK_LABEL, BookingForm, useBookingDrawer } from './Calendar.jsx';

const FSTAT = {
  free: <span className="chip c-free">ว่าง</span>,
  out: <span className="chip c-out">ติดสัญญา</span>,
  fix: <span className="chip c-fix">ส่งซ่อม</span>,
};

// ปฏิทินรายชิ้น (ใน drawer)
function UnitCal({ unit }) {
  const { bookings } = useData();
  const openBooking = useBookingDrawer();
  const [offset, setOffset] = useState(0);
  const today = todayISO();
  const t = parseD(today);
  const base = new Date(t.getFullYear(), t.getMonth() + offset, 1);
  const y = base.getFullYear(), m = base.getMonth();
  const first = new Date(y, m, 1).getDay(), days = new Date(y, m + 1, 0).getDate();
  const bookingOn = (ds) => bookings.find(b => b.rental_unit_id === unit.id && ds >= b.from_date && ds <= b.to_date);

  const cells = [];
  for (let i = 0; i < first; i++) cells.push(<div key={'p' + i} className="calday pad" />);
  for (let d = 1; d <= days; d++) {
    const ds = iso(new Date(y, m, d));
    const b = bookingOn(ds);
    const cls = ['calday', b ? b.kind : '', ds === today ? 'today' : '', ds < today && !b ? 'past' : ''].filter(Boolean).join(' ');
    cells.push(
      <div key={d} className={cls} style={b ? { cursor: 'pointer' } : undefined}
        title={thDate(ds) + ' — ' + (b ? `${BK_LABEL[b.kind]} · ${b.customer_name || ''} (${b.ref_no})` : 'ว่าง')}
        onClick={() => b && openBooking(b)}>{d}</div>
    );
  }
  return (
    <div className="cal">
      <div className="calnav">
        <button onClick={() => setOffset(o => o - 1)}>‹</button>
        <span className="m">{TH_M[m]} {y + 543}</span>
        <button onClick={() => setOffset(o => o + 1)}>›</button>
      </div>
      <div className="calgrid">
        {TH_D.map(d => <div key={d} className="caldow">{d}</div>)}
        {cells}
      </div>
      <div className="callegend">
        <span><i />ว่าง</span><span><i className="out" />กำลังเช่า</span>
        <span><i className="booked" />จองแล้ว</span><span><i className="maint" />ปิดซ่อม</span>
      </div>
    </div>
  );
}

function UnitDrawerBody({ unit, breakEven }) {
  const { bookingsOfUnit, refresh } = useData();
  const openBooking = useBookingDrawer();
  const { can } = useAuth();
  const { toast, closeDrawer } = useUI();
  const today = todayISO();
  const queue = bookingsOfUnit(unit.id).filter(b => b.to_date >= today);
  const [showBook, setShowBook] = useState(false);
  const [busy, setBusy] = useState(false);

  // ปิด/เปิด "สินค้ารุ่นนี้" ทั้ง SKU — ของจิปาถะในคลังเดโม่ (อะไหล่ เชือก) ไม่ควรโผล่ให้เลือกเช่า
  const setRentable = async (on) => {
    setBusy(true);
    try {
      await rpc('rp_catalog_set_rentable', { p_sku: unit.sku, p_rentable: on });
      toast(on ? `เปิดให้เช่า ${unit.name} แล้ว` : `ปิด ${unit.name} ไม่ให้เลือกเช่าแล้ว — ชิ้นที่มีอยู่ยังอยู่ในคลัง`);
      closeDrawer();
      await refresh();
    } catch (e) { toast(e.message); }
    setBusy(false);
  };

  return (<>
    <div className="dw-hero"><Pimg cat={unit.category} size={110} image={unit.image_url} /></div>
    {unit.sku_rentable === false && (
      <div className="note show" style={{ marginTop: 10 }}>
        สินค้ารุ่นนี้ถูกปิดไม่ให้ปล่อยเช่า — ไม่โผล่ในตัวเลือกตอนเปิดสัญญา
      </div>
    )}
    <Sec>ข้อมูล</Sec>
    <Row k="SKU" v={unit.sku} />
    {unit.wh_branch && <Row k="คลังต้นทาง" v={unit.wh_branch === 'w0013' ? 'Demo 71' : unit.wh_branch === 'w0025' ? 'Demo RED' : unit.wh_branch} />}
    <Row k="สถานะ" v={unit.status === 'free' ? 'ว่าง' : unit.status === 'out' ? 'ติดสัญญา' : 'ส่งซ่อม'} />
    <Row k="รอบการใช้งาน" v={`${unit.trips}/${breakEven}`} gold={unit.trips >= breakEven} />
    <Row k="เรทเช่า/ทริป" v={B(unit.trip_rate) + ' ฿'} />
    <Row k="ราคาป้ายปัจจุบัน" v={B(unit.retail) + ' ฿'} />
    <Row k="มูลค่าจำหน่ายสภาพดี" v={'~' + B(Math.round(unit.retail * .6)) + ' ฿'} />
    <Sec>ปฏิทินชิ้นนี้</Sec>
    <UnitCal unit={unit} />
    <Sec>คิวจองล่วงหน้า</Sec>
    {queue.length ? (
      <div className="bklist">
        {queue.map(b => (
          <div className="bkrow" key={b.id} style={{ cursor: 'pointer' }} onClick={() => openBooking(b)}>
            <span className={'tag ' + b.kind}>{BK_LABEL[b.kind]}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{b.customer_name || '—'}</div>
              <div className="dt">{thDate(b.from_date)} – {thDate(b.to_date)} · {b.ref_no}</div>
            </div>
          </div>
        ))}
      </div>
    ) : <div className="empty" style={{ padding: '14px 0' }}>ไม่มีคิวจองล่วงหน้า</div>}
    {can('rent.book') && (showBook
      ? <BookingForm preUnit={unit.id} onDone={() => setShowBook(false)} />
      : <div className="dw-act"><button onClick={() => setShowBook(true)}>จองล่วงหน้า / ปิดซ่อมชิ้นนี้ <span className="ar">→</span></button></div>
    )}
    {can('system.edit') && (
      <div className="dw-act">
        <button disabled={busy} onClick={() => setRentable(unit.sku_rentable === false)}>
          {unit.sku_rentable === false
            ? 'เปิดให้ปล่อยเช่าอีกครั้ง (ทั้งรุ่น)'
            : 'ไม่ให้ปล่อยเช่ารุ่นนี้ (ซ่อนจากตัวเลือกเปิดสัญญา)'}
          <span className="ar">→</span>
        </button>
      </div>
    )}
  </>);
}

export default function Fleet() {
  const { units, refresh } = useData();
  const { settings, can } = useAuth();
  const { openDrawer, toast } = useUI();
  const [cat, setCat] = useState('ทั้งหมด');
  const [q, setQ] = useState('');
  const [syncing, setSyncing] = useState(false);
  const breakEven = Number(settings.break_even_trips ?? 10);

  const s = q.toLowerCase().trim();
  const match = (u) => !s || (u.name + ' ' + u.sku + ' ' + u.unit_code).toLowerCase().includes(s);
  const shown = units.filter(match);
  const cats = ['ทั้งหมด', ...new Set(shown.map(u => u.category))];
  const show = cats.filter(c => c !== 'ทั้งหมด').filter(c => cat === 'ทั้งหมด' || c === cat);

  const openUnit = (u) => openDrawer('คลังเช่า · ' + u.sku, u.name + ' · ' + u.unit_code,
    <UnitDrawerBody unit={u} breakEven={breakEven} />);

  // ดึงสินค้า + จำนวนชิ้นล่าสุดจาก StockPoint (คลังเดโม่) — เพิ่มชิ้นได้ ไม่ลบชิ้นเดิม
  const syncStock = async () => {
    setSyncing(true);
    try {
      const r = await rpc('rp_sync_stockpoint');
      toast(`Sync เสร็จ — สินค้าใหม่ ${r.new_skus} · อัปเดต ${r.updated_skus} · ชิ้นใหม่ ${r.new_units}` +
        (r.deactivated ? ` · ปิด ${r.deactivated} รายการที่ไม่มีของในคลังแล้ว` : '') +
        (r.drift?.length ? ` · ชิ้นในระบบมากกว่าคลัง ${r.drift.length} รายการ` : ''));
      await refresh();
    } catch (e) { toast(e.message); }
    setSyncing(false);
  };

  return (<>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
      <div>
        <h2>คลังเช่า</h2>
        <div className="hint">ทุกชิ้นมีตัวนับรอบการใช้งาน — ครบ {breakEven} รอบถือว่าคุ้มค่าลงทุน พร้อมส่งต่อเป็นสินค้าสภาพดีได้</div>
      </div>
      {can('system.edit') && (
        <button className="btn ghost sm" style={{ marginLeft: 'auto' }} disabled={syncing} onClick={syncStock}>
          {syncing ? 'กำลังดึงข้อมูล…' : 'ดึงสต๊อกล่าสุดจาก StockPoint'}
        </button>
      )}
    </div>
    <div className="pickbar">
      <input className="picksearch" placeholder="ค้นหาชื่ออุปกรณ์ หรือ SKU…" value={q}
        onChange={e => { setQ(e.target.value); setCat('ทั้งหมด'); }} />
      <span className="hint" style={{ margin: 0, whiteSpace: 'nowrap' }}>
        {shown.length} ชิ้น · ว่าง {shown.filter(u => u.status === 'free').length}
      </span>
    </div>
    <div className="catfilter">
      {cats.map(c => (
        <button key={c} className={c === cat ? 'on' : ''} onClick={() => setCat(c)}>
          {c}{c === 'ทั้งหมด' ? '' : ' · ' + shown.filter(u => u.category === c).length}
        </button>
      ))}
    </div>
    {!shown.length && <div className="empty">ไม่พบอุปกรณ์ที่ค้นหา</div>}
    {show.map(c => {
      const list = shown.filter(u => u.category === c);
      const free = list.filter(u => u.status === 'free').length;
      return (
        <div className="catgroup" key={c}>
          <div className="cathead"><h3>{c}</h3>
            <span className="cnt">{list.length} ชิ้น</span><span className="free">ว่าง {free}</span></div>
          <div className="grid g3">
            {list.map(u => {
              const pct = Math.min(100, Math.round(u.trips / breakEven * 100));
              const done = u.trips >= breakEven;
              return (
                <div className="card fleet-card" key={u.id} onClick={() => openUnit(u)}>
                  <div className="pthumb"><Pimg cat={u.category} size={74} image={u.image_url} /></div>
                  <div className="top">
                    <div>
                      <div className="nm">{u.name} <span className="sku">· {u.unit_code}</span></div>
                      <div className="sku"><span className="skutag">{u.sku}</span></div>
                    </div>
                    {u.sku_rentable === false
                      ? <span className="chip c-fix">ไม่ให้เช่า</span>
                      : FSTAT[u.status]}
                  </div>
                  <div className="trip">
                    <div className="lbl"><span>{done ? 'ครบรอบคุ้มทุนแล้ว' : 'รอบการใช้งาน'}</span><b>{u.trips}/{breakEven}</b></div>
                    <div className="bar"><i className={done ? 'done' : ''} style={{ width: pct + '%' }} /></div>
                  </div>
                  <div className="resale">มูลค่าจำหน่ายสภาพดี ~<b>{B(Math.round(u.retail * .6))} ฿</b></div>
                </div>
              );
            })}
          </div>
        </div>
      );
    })}
  </>);
}
